import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./TransactionDashboard.css";

const TransactionDashboard = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get("/mpesa/transactions");
      console.log(
        "Fetched transactions:",
        JSON.stringify(response.data, null, 2)
      );
      setTransactions(response.data);
      setLoading(false);
    } catch (err) {
      setError("Failed to load transactions. Please try again.");
      setLoading(false);
      console.error("Error fetching transactions:", err);
    }
  };

  // Fixed categories from OfferingForm
  const fixedCategories = [
    "combinedOffering",
    "ocaChurchBudget",
    "churchBuilding",
    "evangelism",
    "campmeetingOffering",
    "campmeetingExpenses",
    "thanksgivingSabbathSchool",
  ];

  // Fixed predefined departments from OfferingForm
  const fixedDepartments = ["AWM", "AMO", "Children"];

  // Get all unique "Other" department labels
  const getOtherDepartmentHeaders = (transactions) => {
    const otherDeptNames = new Set();
    transactions.forEach((tx) => {
      if (tx.departments?.other) {
        tx.departments.other.forEach((item) => otherDeptNames.add(item.label));
      }
    });
    return Array.from(otherDeptNames);
  };

  // Export to JSON
  const exportToJSON = () => {
    const jsonData = JSON.stringify(transactions, null, 2);
    const blob = new Blob([jsonData], { type: "application/json" });
    saveAs(blob, "transactions.json");
  };

  // Export to Excel aligned with OfferingForm, using 0 for empty values
  const exportToExcel = () => {
    const otherHeaders = getOtherDepartmentHeaders(transactions);
    const exportData = [];

    // First row: Main headings
    const firstRow = [
      ...Array(6).fill("Transaction Details"), // Covers Phone Number to M-Pesa Receipt
      ...Array(fixedCategories.length).fill("Categories"),
      ...Array(fixedDepartments.length).fill("Departments"),
      ...Array(otherHeaders.length).fill("Other Departments"),
    ];

    // Second row: Specific field names with human-readable labels
    const secondRow = [
      "Phone Number",
      "Total Amount (KES)",
      "Status",
      "Payment Date",
      "Transaction Date",
      "M-Pesa Receipt",
      ...fixedCategories.map((cat) => {
        const labelMap = {
          combinedOffering: "Combined Offering 10%+",
          ocaChurchBudget: "OCA Church Budget",
          churchBuilding: "Church Building",
          evangelism: "Evangelism",
          campmeetingOffering: "Campmeeting Offering",
          campmeetingExpenses: "Campmeeting Expenses",
          thanksgivingSabbathSchool: "Thanksgiving Sabbath School",
        };
        return labelMap[cat];
      }),
      ...fixedDepartments,
      ...otherHeaders,
    ];

    // Data rows
    const dataRows = transactions.map((tx) => {
      const row = [
        tx.phone || "N/A",
        tx.totalAmount ? tx.totalAmount.toFixed(2) : "N/A",
        tx.status || "N/A",
        tx.paymentDate ? new Date(tx.paymentDate).toLocaleString() : "N/A",
        tx.transactionDate
          ? new Date(tx.transactionDate).toLocaleString()
          : "N/A",
        tx.mpesaReceiptNumber || "N/A",
      ];

      // Add fixed category amounts (0 if not present)
      fixedCategories.forEach((cat) => {
        row.push(
          tx.categories && tx.categories[cat] && tx.categories[cat].amount
            ? tx.categories[cat].amount
            : 0
        );
      });

      // Add fixed department amounts (0 if not present)
      fixedDepartments.forEach((dept) => {
        row.push(
          tx.departments &&
            tx.departments.predefined &&
            tx.departments.predefined[dept]
            ? tx.departments.predefined[dept]
            : 0
        );
      });

      // Add other department amounts (0 if not present)
      otherHeaders.forEach((otherDept) => {
        const otherEntry = tx.departments?.other?.find(
          (item) => item.label === otherDept
        );
        row.push(otherEntry ? otherEntry.amount : 0);
      });

      return row;
    });

    // Combine headers and data
    exportData.push(firstRow);
    exportData.push(secondRow);
    exportData.push(...dataRows);

    // Create and style worksheet
    const ws = XLSX.utils.aoa_to_sheet(exportData);
    ws["!cols"] = [
      { wch: 15 }, // Phone Number
      { wch: 15 }, // Total Amount
      { wch: 10 }, // Status
      { wch: 20 }, // Payment Date
      { wch: 20 }, // Transaction Date
      { wch: 15 }, // M-Pesa Receipt
      ...fixedCategories.map(() => ({ wch: 20 })),
      ...fixedDepartments.map(() => ({ wch: 10 })),
      ...otherHeaders.map(() => ({ wch: 15 })),
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");
    XLSX.writeFile(wb, "transactions.xlsx");
  };

  if (loading) return <div className="loading">Loading transactions...</div>;
  if (error) return <div className="error">{error}</div>;

  const otherHeaders = getOtherDepartmentHeaders(transactions);

  return (
    <div className="dashboard">
      <h1>Transaction Dashboard</h1>
      <div className="export-buttons">
        <button onClick={exportToJSON}>Export to JSON</button>
        <button onClick={exportToExcel}>Export to Excel</button>
      </div>
      <table>
        <thead>
          <tr>
            {/* First row: Group headings */}
            <th colSpan={6}>Transaction Details</th>
            <th colSpan={fixedCategories.length}>Categories</th>
            <th colSpan={fixedDepartments.length}>Departments</th>
            <th colSpan={otherHeaders.length}>Other Departments</th>
          </tr>
          <tr>
            {/* Second row: Specific field names */}
            <th>Phone Number</th>
            <th>Total Amount (KES)</th>
            <th>Status</th>
            <th>Payment Date</th>
            <th>Transaction Date</th>
            <th>M-Pesa Receipt</th>
            {fixedCategories.map((cat) => (
              <th key={cat}>
                {cat === "combinedOffering" && "Combined Offering 10%+"}
                {cat === "ocaChurchBudget" && "OCA Church Budget"}
                {cat === "churchBuilding" && "Church Building"}
                {cat === "evangelism" && "Evangelism"}
                {cat === "campmeetingOffering" && "Campmeeting Offering"}
                {cat === "campmeetingExpenses" && "Campmeeting Expenses"}
                {cat === "thanksgivingSabbathSchool" &&
                  "Thanksgiving Sabbath School"}
              </th>
            ))}
            {fixedDepartments.map((dept) => (
              <th key={dept}>{dept}</th>
            ))}
            {otherHeaders.map((other) => (
              <th key={other}>{other}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {transactions.map((tx) => (
            <tr key={tx._id || Math.random()}>
              <td>{tx.phone || "N/A"}</td>
              <td>{tx.totalAmount ? tx.totalAmount.toFixed(2) : "N/A"}</td>
              <td>{tx.status || "N/A"}</td>
              <td>
                {tx.paymentDate
                  ? new Date(tx.paymentDate).toLocaleString()
                  : "N/A"}
              </td>
              <td>
                {tx.transactionDate
                  ? new Date(tx.transactionDate).toLocaleString()
                  : "N/A"}
              </td>
              <td>{tx.mpesaReceiptNumber || "N/A"}</td>
              {fixedCategories.map((cat) => (
                <td key={cat}>
                  {tx.categories &&
                  tx.categories[cat] &&
                  tx.categories[cat].amount
                    ? tx.categories[cat].amount
                    : 0}
                </td>
              ))}
              {fixedDepartments.map((dept) => (
                <td key={dept}>
                  {tx.departments &&
                  tx.departments.predefined &&
                  tx.departments.predefined[dept]
                    ? tx.departments.predefined[dept]
                    : 0}
                </td>
              ))}
              {otherHeaders.map((otherDept) => {
                const otherEntry = tx.departments?.other?.find(
                  (item) => item.label === otherDept
                );
                return (
                  <td key={otherDept}>{otherEntry ? otherEntry.amount : 0}</td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TransactionDashboard;
